import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import InfluencerPost from './InfluencerPost';
import InfluencerPostContext from '../context/InfluencerPostContext';
import InfluencerPostCarouselControl from './influencer-post-carousel-control/InfluencerPostCarouselControl';

import { deletePosts } from 'redux-store/reducers/instagramPostsDeleteReducer';

import { NUM_POSTS_VISIBLE } from 'utils/influencers';
import { getInstagramThumbnailUrl } from 'utils/content/thumbnails';
import {
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  getYoutubeVideoThumbnail,
  isInstagramChannel,
  isTiktokChannel
} from 'utils/channels';
import { getPostCodeFromUrl, getPostsByAvailability, getTikTokVideoCodeFromUrl } from 'utils/posts';
import { getTikTokThumbnailUrl } from 'utils/tiktok-thumbnail';

import type { HelperInstagramImageSize, Post } from 'types';
import type { ConsentReducerConsentsMap } from 'redux-store/types/ConsentReducer.type';

type OwnProps = {
  title: string,
  channelType: string,
  posts: Array<Post>,
  numImages: number,
  numFollowers: number
};

type StateProps = {
  userChannelConsents: ConsentReducerConsentsMap
};

type DispatchProps = {
  dispatchDeleteInstagramPosts: (ids: Array<string>) => void,
  dispatchDeleteTikTokPosts: (ids: Array<string>) => void
};

type Props = OwnProps & DispatchProps & StateProps;

type State = {
  posts: Array<Post>,
  carouselIndex: number
};

class InfluencerPosts extends React.PureComponent<Props, State> {
  static defaultProps = {
    numImages: NUM_POSTS_VISIBLE.instagram,
    channelType: 'instagram'
  };

  state = {
    posts: [] as Array<Post>,
    carouselIndex: 0
  };

  componentDidMount() {
    this.fetchImages();
  }

  fetchImages(): void {
    this.preloadImages().then(posts => {
      this.setState({ posts });
    });
  }

  async preloadImages(): Promise<Array<Post>> {
    const { posts, channelType, numImages: requiredPostsCount, userChannelConsents } = this.props;
    const { carouselIndex } = this.state;
    const startIndex = carouselIndex * requiredPostsCount;
    let getPostByUrl: (videoUrl: string, size: HelperInstagramImageSize, post: Post) => string | Promise<string> = getYoutubeVideoThumbnail;

    if (isInstagramChannel(channelType)) {
      getPostByUrl = getInstagramThumbnailUrl;
    }

    if (isTiktokChannel(channelType)) {
      getPostByUrl = getTikTokThumbnailUrl;
    }

    // BE won't provide us proper image urls anytime soon
    const mappedPosts = posts.map(item => ({ ...item, imageUrl: '' }));

    if (!userChannelConsents[channelType]) {
      // if has no user consent for this channel - use posts without loading images
      return mappedPosts;
    }

    const { unavailablePosts, availablePosts } = await getPostsByAvailability(mappedPosts, requiredPostsCount, getPostByUrl, undefined, undefined, startIndex);
    this.deleteUnavailablePosts(unavailablePosts);

    return availablePosts;
  }

  deleteUnavailablePosts = (posts: Array<Post>): void => {
    if (isEmpty(posts)) {
      return;
    }

    const { dispatchDeleteInstagramPosts, dispatchDeleteTikTokPosts, channelType } = this.props;

    if (channelType === CHANNEL_TYPE_INSTAGRAM) {
      const postsCodes = posts.map(post => getPostCodeFromUrl(post.url));
      dispatchDeleteInstagramPosts(postsCodes);
    }

    if (channelType === CHANNEL_TYPE_TIKTOK) {
      const postsCodes = posts.map(post => getTikTokVideoCodeFromUrl(post.url));
      dispatchDeleteTikTokPosts(postsCodes);
    }
  };

  handleCarouselIndexChange = (carouselIndex: number): void => {
    this.setState(prevState => ({
      ...prevState,
      carouselIndex
    }), () => this.fetchImages());
  };

  renderInfluencerPost(): React.ReactNode {
    const { channelType, numImages, numFollowers, userChannelConsents } = this.props;
    const { posts } = this.state;

    return posts.slice(0, numImages).map((post, index) => (
      <InfluencerPost
        key={`influencer-posts-${channelType}-${String(post.url || post.imageUrl || post.assetUrl)}`}
        hasConsent={userChannelConsents[channelType]}
        post={post}
        size={channelType === 'instagram' ? 'normal' : 'bigger'}
        type={channelType}
        numFollowers={numFollowers}
        style="card"
        index={index}
      />
    ));
  }

  render(): React.ReactNode {
    const { title, numImages, posts: originalPosts } = this.props;
    const { posts, carouselIndex } = this.state;

    if (isEmpty(posts)) {
      return null;
    }

    const postsContainerClasses = classnames({
      'influencer-posts__container': true,
      'influencer-posts__container--limited': posts.length < numImages
    });

    return (
      <div className="influencer-posts">
        {
          title && (
            <div className="influencer-posts__title">
              {title}
            </div>
          )
        }
        <div className={postsContainerClasses}>
          <InfluencerPostContext.Provider value={posts.slice(0, numImages)}>
            {this.renderInfluencerPost()}
          </InfluencerPostContext.Provider>
          <InfluencerPostCarouselControl
            key="influencer-post-carousel__controls--prev"
            direction="prev"
            isDisabled={carouselIndex === 0}
            carouselIndex={carouselIndex}
            onSetCarouselIndex={this.handleCarouselIndexChange}
            skipTooltip
          />
          <InfluencerPostCarouselControl
            key="influencer-post-carousel__controls--next"
            direction="next"
            isDisabled={(carouselIndex + 1) * numImages >= originalPosts.length || posts.length < numImages}
            carouselIndex={carouselIndex}
            onSetCarouselIndex={this.handleCarouselIndexChange}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ consentReducer }): StateProps => ({
  userChannelConsents: consentReducer.consents
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDeleteInstagramPosts: (posts: Array<string>) => (
    dispatch(deletePosts(posts, 'instagram'))
  ),
  dispatchDeleteTikTokPosts: (posts: Array<string>) => (
    dispatch(deletePosts(posts, 'tiktok'))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(InfluencerPosts);
